import queryString from "query-string";

import {
  BUSINESS,
  CREATE_BUSINESS_FROM_IMPORT,
  DELETE_BUSINESS_NO_LONGER_EXISTS,
  GLOBAL_BUSINESSES,
  MERGE_BUSINESSES,
  PATCH_BUSINESS_FROM_IMPORT,
  REQUERY_ABN_FOR_BUSINESS,
  SET_BUSINESS_IMPORT_MAPPING,
} from "constants/actionTypes";

import { getLivestockSaleId } from "lib/navigation";
import {
  serializeBusiness,
  serializeGlobalBusinesses,
} from "lib/serializers/businesses";

import { serializeBusinessMergeEvents } from "lib/serializers/businessMergeEvents";
import { offlineActionCreator, urlCheckAndAddIdAndAction } from "./lib";
import { URLCreatorProps } from "./types";

export const deleteBusinessNoLongerExists = id => ({
  type: DELETE_BUSINESS_NO_LONGER_EXISTS,
  meta: {
    business_id: id,
  },
});

export const createBusinessFromImport = (payload, externalCode, source) => ({
  type: CREATE_BUSINESS_FROM_IMPORT,
  payload,
  externalCode,
  source,
});

export const patchBusinessFromImport = (
  businessId,
  payload,
  shouldOverrideShortCode,
) => ({
  type: PATCH_BUSINESS_FROM_IMPORT,
  businessId,
  payload,
  shouldOverrideShortCode,
});

export const setBusinessImportMapping = (source, externalCode, businessId) => ({
  type: SET_BUSINESS_IMPORT_MAPPING,
  source,
  externalCode,
  businessId,
});

// Note - we're explicitly looking for `saleyardNameFilter` rather than the globally set `saleyardName` from the URL
const urlCreator = (props: URLCreatorProps = {}) => {
  const { id, action, changesSince, saleyardNameFilter } = props;
  return queryString.stringifyUrl({
    url: urlCheckAndAddIdAndAction("/v2/businesses/", id, action),
    query: {
      changesSince,
      saleyard__name: saleyardNameFilter,
    },
  });
};

export const BusinessAction = offlineActionCreator(
  BUSINESS,
  serializeBusiness,
  urlCreator,
  false,
);

// Inject the current sale id into the create payload.
// A bit nasty, but means changes to the generic create are not lost.
const originalCreateAction = BusinessAction.create;

BusinessAction.create = (
  deserializedPayload,
  options = {
    suppressToast: false,
    suppressErrorToast: false,
    suppressSuccessToast: false,
    changeReason: null,
    saleyardName: null,
  },
) => {
  const extendedPayload = {
    ...deserializedPayload,
    addedInLivestockSaleId: getLivestockSaleId(),
  };
  return originalCreateAction(extendedPayload, options);
};

BusinessAction.suggestBuyerWayProperty = (
  businessId,
  buyerWayName,
  propertyId,
) => ({
  type: BUSINESS.SUGGEST_BUYER_WAY_PROPERTY.ACTION,
  businessId,
  buyerWayName,
  propertyId,
});

BusinessAction.getBusinessesForAutoComplete = (
  term,
  userRole,
  options = {},
) => ({
  type: BUSINESS.GET_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
  term,
  userRole,
  options,
});

BusinessAction.getGlobalBusinessesForAutoComplete = (term, options = {}) => ({
  type: BUSINESS.GET_GLOBAL_BUSINESSES_FOR_AUTO_COMPLETE.REQUEST,
  term,
  options,
});

BusinessAction.getOrCreateGlobalBusiness = globalBusinessId => ({
  type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.REQUEST,
  meta: {
    offline: {
      effect: {
        url: urlCreator({
          id: globalBusinessId,
          action: "get-or-create-from-global",
        }),
        method: "POST",
        body: JSON.stringify({}),
      },
      commit: {
        type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.SUCCESS,
        id: globalBusinessId,
        suppressSuccessToast: true,
      },
      rollback: {
        type: BUSINESS.GET_OR_CREATE_GLOBAL_BUSINESS.FAILURE,
      },
    },
  },
});

export const requeryABNForBusiness = (cbid, saleyardName) => {
  const payload = { cbid };
  const meta = { id: cbid, saleyardName };

  const url = `/v2/businesses/${cbid}/requery-abn/`;

  return {
    type: REQUERY_ABN_FOR_BUSINESS.REQUEST,
    payload,
    meta: {
      id: cbid,
      offline: {
        effect: {
          url,
          method: "POST",
          body: JSON.stringify(payload),
        },
        commit: {
          type: REQUERY_ABN_FOR_BUSINESS.SUCCESS,
          meta,
        },
        rollback: {
          type: REQUERY_ABN_FOR_BUSINESS.ROLLBACK,
          meta,
        },
      },
    },
  };
};

export const GlobalBusinessesAction = offlineActionCreator(
  GLOBAL_BUSINESSES,
  serializeGlobalBusinesses,
  () => "/v2/global-businesses/",
  false,
);

export const MergeBusinessesAction = offlineActionCreator(
  MERGE_BUSINESSES,
  serializeBusinessMergeEvents,
  ({ targetBusinessId, sourceBusinessId }) =>
    `/v2/businesses/${targetBusinessId}/merge/${sourceBusinessId}/`,
  false,
);
